// Surveille /version.json et déclenche la recharge aux moments sûrs « retour
// sur l'onglet » et « inactivité ». Le changement de module, lui, appelle
// reloadIfUpdatePending directement (AppUpdateWatcher, OwnerDashboard).
import type { VersionInfo } from "./buildInfo";
import { compareVersion, fetchRemoteVersion, getLocalVersion } from "./version";
import { reportRemoteVersion } from "./store";
import { reloadIfUpdatePending, type ReloadReason } from "./reloadPolicy";

export const FIRST_CHECK_DELAY_MS = 30 * 1000;
export const CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const HIDDEN_MIN_MS = 10 * 60 * 1000;
export const IDLE_MS = 30 * 60 * 1000;

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

export interface WatcherDeps {
  now: () => number;
  fetchRemote: () => Promise<VersionInfo | null>;
  getLocal: () => VersionInfo;
  reload: (reason: ReloadReason) => boolean;
  document: Document;
  window: Window;
}

export function startAppUpdateWatcher(overrides: Partial<WatcherDeps> = {}): () => void {
  const d: WatcherDeps = {
    now: () => Date.now(),
    fetchRemote: () => fetchRemoteVersion(),
    getLocal: getLocalVersion,
    reload: (reason) => reloadIfUpdatePending(reason),
    document,
    window,
    ...overrides,
  };

  let stopped = false;
  let lastActivity = d.now();
  let hiddenAt: number | null = d.document.visibilityState === "hidden" ? d.now() : null;

  // Rend true seulement si le serveur a répondu PENDANT ce cycle : une mise à
  // jour connue d'avant ne suffit pas pour recharger, le serveur doit être
  // joignable maintenant (sinon réveil hors ligne = page d'erreur du navigateur).
  const check = async (): Promise<boolean> => {
    if (stopped || d.document.visibilityState === "hidden") return false;
    const remote = await d.fetchRemote();
    if (stopped || !remote) return false;
    reportRemoteVersion(compareVersion(d.getLocal(), remote), remote.buildId);
    return true;
  };

  const tick = (): void => {
    void check().then((reachable) => {
      if (reachable && !stopped && d.now() - lastActivity >= IDLE_MS) d.reload("idle");
    });
  };

  const onActivity = (): void => {
    lastActivity = d.now();
  };

  const onVisibility = (): void => {
    if (stopped) return;
    if (d.document.visibilityState === "hidden") {
      hiddenAt = d.now();
      return;
    }
    const hiddenFor = hiddenAt === null ? 0 : d.now() - hiddenAt;
    hiddenAt = null;
    void check().then((reachable) => {
      if (reachable && !stopped && hiddenFor >= HIDDEN_MIN_MS) d.reload("visible");
    });
  };

  const firstTimer = setTimeout(tick, FIRST_CHECK_DELAY_MS);
  const interval = setInterval(tick, CHECK_INTERVAL_MS);
  ACTIVITY_EVENTS.forEach((type) => d.window.addEventListener(type, onActivity, { capture: true, passive: true }));
  d.document.addEventListener("visibilitychange", onVisibility);

  return () => {
    stopped = true;
    clearTimeout(firstTimer);
    clearInterval(interval);
    ACTIVITY_EVENTS.forEach((type) => d.window.removeEventListener(type, onActivity, { capture: true }));
    d.document.removeEventListener("visibilitychange", onVisibility);
  };
}
